import { Redirect, Route } from 'react-router-dom';
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  isPlatform,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { bookOutline, calculatorOutline, homeOutline } from 'ionicons/icons';
/* import { App } from '@capacitor/app'; */
import { Start, CalculatorOverview, ArticleIntro, ArticleDetail, CuttingDataStd, CuttingDataTro, Toolfinder, Imprint, Privacy } from './pages';
import { differenceInCalendarDays } from 'date-fns';
import { SplashScreen } from '@capacitor/splash-screen';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/palettes/dark.class.css'

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import "./pages/style.scss";

/* Theme variables */
import './theme/variables.css';

import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from './data/store';
import { Suspense, useEffect, useMemo } from 'react';
/* import { getInitialDarkModeVal } from './data/slices/user'; */
import { createTheme, ThemeProvider } from '@mui/material';
import { muiThemeColorsDark, muiThemeColorsLight } from './theme/mui-theme';
import { Network } from '@capacitor/network';
import { setIsOnline } from './data/slices/network';
import { getInitialLoginValues } from './data/slices/auth';
import { getInitialDBTimestamp, setDBUpdating } from './data/slices/db';
import ProductPriceAvail from './pages/productPriceAvail/ProductPriceAvail';
import { setStatusBarStyleDark, setStatusBarStyleLight } from './theme/statusBar';
import { mapTableUpdateFunctions, updateTables } from './data/db/db';
import { isWeb } from './util/platform';
import React from 'react';
import CuttingDataPreselection from './pages/cuttingDataPreselection/CuttingDataPreselection';
import { TimestampDB } from './models/articleModel';
import ArticleService from './data/services/ArticleService';
import Calculator from './pages/calculator/Calculator';
import { get } from './util/storage';
import { setDarkModeAndStorage } from './data/slices/user';
import { useTranslation } from 'react-i18next';

setupIonicReact({});

const IonicApp: React.FC = () => {
  const darkMode: boolean = useSelector((state: RootState) => state.user.darkMode)
  const dispatch = useAppDispatch();
  const isOnline: boolean = useSelector((state: RootState) => state.network.isOnline);
  const dbTimestamps: TimestampDB[] = useSelector((state: RootState) => state.db.dbTimestamps);
  const dbLoading: boolean = useSelector((state: RootState) => state.db.dbLoading);
  const dbUpdating: boolean = useSelector((state: RootState) => state.db.dbUpdating);
  const { t } = useTranslation();

  // native Back Button functionality
  /* App.addListener('backButton', (ev) => {
    console.log(ev)
    if (!ev.canGoBack) {
      App.exitApp();
    }
  }); */


  useEffect(() => {
    SplashScreen.hide({ fadeOutDuration: 200 })
  }, [])

  // get initial state values for app
  useEffect(() => {
    /* dispatch(getInitialDarkModeVal()); */
    dispatch(getInitialLoginValues());
    dispatch(getInitialDBTimestamp());
  }, [dispatch])

  // Network Status
  useEffect(() => {
    const getInitialNetworkState = async () => {
      const status = await Network.getStatus();
      dispatch(setIsOnline(status.connected))
    }
    getInitialNetworkState();
    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
      dispatch(setIsOnline(status.connected));
    });
    return () => {
      Network.removeAllListeners();
    };
  }, [dispatch])




  useEffect(() => {
    let ignore = false;

    const intializeTheming = (isDark: boolean) => {
      dispatch(setDarkModeAndStorage(isDark))
      document.documentElement.classList.toggle('ion-palette-dark', isDark);
    }

    const getInitialDarkMode = async () => {
      let res = await get("darkMode");
      if (res !== null && !ignore) {
        intializeTheming(res)
      } else if (!ignore) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
        intializeTheming(prefersDark.matches)
      }
    }
    getInitialDarkMode()


    return () => { ignore = true }

  }, [dispatch]);





  // fetch Data if DB is to old and network is available
  useEffect(() => {
    //updateAllTables(); 
    let ignore = false;

    const tablesWhoNeedUpdates = async () => {
      const externalTimestampsRequest = await Promise.all([
        ArticleService.getArticlesCuttingParamsStdTimestamp(),
        ArticleService.getArticlesCuttingParamsTroTimestamp()
      ])
      const externalTimestamps = Object.assign({}, ...externalTimestampsRequest)

      let updateList: (Promise<void> | undefined)[] = []
      dbTimestamps.forEach((timestampEntry) => {
        if (timestampEntry.name === "articlesCuttingParamsStd") {
          if (new Date(timestampEntry.timestamp) < new Date(externalTimestamps.articlesCuttingParamsStd)) {
            //Update
            if (!ignore) {
              updateList.push(mapTableUpdateFunctions(timestampEntry.name))
            }
          }
        } else if (timestampEntry.name === "articlesCuttingParamsTro") {
          if (new Date(timestampEntry.timestamp) < new Date(externalTimestamps.articlesCuttingParamsTro)) {
            //Update
            if (!ignore) {
              updateList.push(mapTableUpdateFunctions(timestampEntry.name))
            }
          }
        } else {
          let timestamp = new Date(timestampEntry.timestamp);
          if (differenceInCalendarDays(new Date(), timestamp) > 1) {
            //Update
            if (!ignore) {
              updateList.push(mapTableUpdateFunctions(timestampEntry.name))
            }
          }
        }
      })
      updateTables(updateList)
    }

    if (isOnline && dbTimestamps.length > 0) {
      dispatch(setDBUpdating(true))
      tablesWhoNeedUpdates()
    }

    return () => { ignore = true }
  }, [dbTimestamps, isOnline, dispatch])

  // dark theme MUI
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          ...(!darkMode) ? muiThemeColorsLight : muiThemeColorsDark
        },
      }),
    [darkMode],
  );

  // dark mode status bar ios
  useEffect(() => {
    if (isPlatform("ios") && !isWeb()) {
      if (darkMode) {
        setStatusBarStyleDark()
      } else {
        setStatusBarStyleLight();
      }
    }
  }, [darkMode])

  return (
    <Suspense fallback="...is loading">
      <ThemeProvider theme={theme}>
        <IonApp /* className={`${darkMode ? 'dark' : ''}`} */>
          <IonReactRouter>
            <IonTabs>
              <IonRouterOutlet>
                <Route exact path="/">
                  <Redirect to="/tabs/start" />
                </Route>
                {/* <Route exact path="/login" render={(props) => <Login {...props} />} /> */}
                <Route exact path="/tabs/start" render={(props) => <Start {...props} />} />
                <Route exact path="/tabs/start/imprint" render={(props) => <Imprint {...props} />} />
                <Route exact path="/tabs/start/privacy" render={(props) => <Privacy {...props} />} />
                <Route exact path="/tabs/calculator" render={(props) => <CalculatorOverview {...props} />} />
                <Route exact path="/tabs/calculator/form/:ident?/:pos?/:matGroup?/:strategy?/:ap?" render={(props) => <Calculator {...props} />} />
                <Route exact path="/tabs/calculator/form/:ident?/:pos?" render={(props) => <Calculator {...props} />} />
                <Route exact path="/tabs/calculator/preselection/:id" render={(props) => <CuttingDataPreselection {...props} />} />
                <Route exact path="/tabs/calculator/preselection/cuttingDataStd/:ident/:pos" render={(props) => <CuttingDataStd {...props} />} />
                <Route exact path="/tabs/calculator/preselection/cuttingDataTro/:ident/:pos" render={(props) => <CuttingDataTro {...props} />} />
                <Route exact path="/tabs/article" render={(props) => <ArticleIntro {...props} />} />
                <Route exact path="/tabs/article/productPriceAvail/:kat" render={(props) => <ProductPriceAvail {...props} />} />
                <Route exact path="/tabs/article/detail/:id" render={(props) => <ArticleDetail {...props} />} />
                <Route exact path="/tabs/article/cuttingDataStd/:ident/:pos" render={(props) => <CuttingDataStd {...props} />} />
                <Route exact path="/tabs/article/cuttingDataTro/:ident/:pos" render={(props) => <CuttingDataTro {...props} />} />
                <Route exact path="/tabs/article/toolfinder" render={(props) => <Toolfinder {...props} />} />

              </IonRouterOutlet>

              <IonTabBar className={!isOnline ? "tabBarOffline" : ""} slot="bottom" id='bottomMenu'>
                <IonTabButton tab="start" href="/tabs/start" >
                  <IonIcon icon={homeOutline} />
                  <IonLabel>{t("Start")}</IonLabel>
                </IonTabButton>
                <IonTabButton tab="calculator" href="/tabs/calculator/" onClick={(e) => {
                  e.preventDefault();
                  e.detail.href = "/tabs/calculator"
                }}>
                  <IonIcon icon={calculatorOutline} />
                  <IonLabel>{t("Schnittwerte")}</IonLabel>
                </IonTabButton>
                <IonTabButton tab="article" href="/tabs/article/">
                  <IonIcon icon={bookOutline} />
                  <IonLabel>{t("Artikel")}</IonLabel>
                </IonTabButton>
              </IonTabBar>
            </IonTabs>
          </IonReactRouter>
          {!isOnline && <div className='offlineIndicator'>{t("Keine Verbindung")}</div>}

          {dbLoading && <div className='offlineIndicator'>{t("Daten werden im Hintergrund geladen")}</div>}
          {dbUpdating && <div className='offlineIndicator'>{t("Daten werden im Hintergrund aktualisiert")}</div>}
        </IonApp>
      </ThemeProvider>
    </Suspense>
  )
};

export default IonicApp;
